import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useTable, useSortBy, usePagination } from 'react-table';
import { toast } from 'react-toastify';

export default function AlertHistoryList({ alertId }) {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [alertName, setAlertName] = useState('');

  useEffect(() => {
    fetchHistory();
  }, [alertId]);

  const fetchHistory = async () => {
    setLoading(true);
    try {
      let url = '/api/alerts/system/history';
      let params = { limit: 100 }; // Add default pagination parameters
      
      if (alertId) {
        url = `/api/alerts/${alertId}/history`;
        
        // Also fetch alert name if alertId is provided
        const alertResponse = await axios.get(`/api/alerts/${alertId}`);
        setAlertName(alertResponse.data.name);
      }
      
      const response = await axios.get(url, { params });
      setHistory(response.data);
    } catch (error) {
      console.error('Error fetching alert history:', error);
      toast.error('Failed to load alert execution history');
    } finally {
      setLoading(false);
    }
  };

  const columns = React.useMemo(
    () => [
      ...(alertId ? [] : [
        {
          Header: 'Alert ID',
          accessor: 'alert_id',
          Cell: ({ value }) => <span className="font-mono">{value}</span>
        }
      ]),
      {
        Header: 'Run Start',
        accessor: 'run_start_time',
        Cell: ({ value }) => new Date(value).toLocaleString()
      },
      {
        Header: 'Run End',
        accessor: 'run_end_time',
        Cell: ({ value }) => new Date(value).toLocaleString()
      },
      {
        Header: 'Status',
        accessor: 'status',
        Cell: ({ value }) => (
          <span className={`px-2 py-1 rounded text-xs font-medium ${value === 'success' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
            {value.toUpperCase()}
          </span>
        )
      },
      {
        Header: 'Affected Nodes',
        accessor: 'affected_nodes_count',
        Cell: ({ value }) => <span className="font-mono">{value}</span>
      },
      {
        Header: 'Execution Time (s)',
        accessor: 'execution_time_seconds',
        Cell: ({ value }) => <span className="font-mono">{value}</span>
      },
      {
        Header: 'Error Message',
        accessor: 'error_message',
        Cell: ({ value }) => (
          value ? (
            <div className="max-w-xs truncate" title={value}>
              <span className="text-red-600">{value}</span>
            </div>
          ) : (
            <span className="text-gray-400">None</span>
          )
        )
      }
    ],
    [alertId]
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    prepareRow,
    page,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    gotoPage,
    nextPage,
    previousPage,
    setPageSize,
    state: { pageIndex, pageSize }
  } = useTable(
    {
      columns,
      data: history,
      initialState: {
        pageIndex: 0,
        pageSize: 10,
        sortBy: [{ id: 'run_start_time', desc: true }]
      }
    },
    useSortBy,
    usePagination
  );

  return (
    <div className="bg-white shadow rounded">
      <h2 className="bg-purple-l border-b border-gray-200 px-6 py-2 text-white font-bold tracking-wider uppercase text-md">
        {alertId ? `Execution History: ${alertName}` : 'System Execution History'}
      </h2>
      
      {loading ? (
        <div className="text-center py-8">Loading history...</div>
      ) : (
        <>
          <div className="overflow-x-auto">
            <table {...getTableProps()} className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                {headerGroups.map(headerGroup => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map(column => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider"
                      >
                        <div className="flex items-center">
                          {column.render('Header')}
                          <span>
                            {column.isSorted
                              ? column.isSortedDesc
                                ? ' 🔽'
                                : ' 🔼'
                              : ''}
                          </span>
                        </div>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()} className="bg-white divide-y divide-gray-200">
                {page.length > 0 ? (
                  page.map(row => {
                    prepareRow(row);
                    return (
                      <tr {...row.getRowProps()} className="hover:bg-gray-50">
                        {row.cells.map(cell => (
                          <td
                            {...cell.getCellProps()}
                            className="px-6 py-4 whitespace-nowrap text-sm"
                          >
                            {cell.render('Cell')}
                          </td>
                        ))}
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan={columns.length} className="px-6 py-4 text-center text-sm text-gray-500">
                      No execution history found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
          
          {/* Pagination */}
          {page.length > 0 && (
            <div className="px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
              <div className="flex-1 flex justify-between sm:hidden">
                <button
                  onClick={() => previousPage()}
                  disabled={!canPreviousPage}
                  className={`relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${!canPreviousPage ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Previous
                </button>
                <button
                  onClick={() => nextPage()}
                  disabled={!canNextPage}
                  className={`ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md ${!canNextPage ? 'bg-gray-100 text-gray-400' : 'bg-white text-gray-700 hover:bg-gray-50'}`}
                >
                  Next
                </button>
              </div>
              <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                <div>
                  <p className="text-sm text-gray-700">
                    Showing <span className="font-medium">{pageIndex * pageSize + 1}</span> to{' '}
                    <span className="font-medium">
                      {Math.min((pageIndex + 1) * pageSize, history.length)}
                    </span>{' '}
                    of <span className="font-medium">{history.length}</span> results
                  </p>
                </div>
                <div>
                  <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                    <button
                      onClick={() => gotoPage(0)}
                      disabled={!canPreviousPage}
                      className={`relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium ${!canPreviousPage ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                      <span className="sr-only">First</span>
                      <span>«</span>
                    </button>
                    <button
                      onClick={() => previousPage()}
                      disabled={!canPreviousPage}
                      className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${!canPreviousPage ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                      <span className="sr-only">Previous</span>
                      <span>‹</span>
                    </button>
                    <span className="relative inline-flex items-center px-4 py-2 border border-gray-300 bg-white text-sm font-medium text-gray-700">
                      Page {pageIndex + 1} of {pageOptions.length}
                    </span>
                    <button
                      onClick={() => nextPage()}
                      disabled={!canNextPage}
                      className={`relative inline-flex items-center px-2 py-2 border border-gray-300 bg-white text-sm font-medium ${!canNextPage ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                      <span className="sr-only">Next</span>
                      <span>›</span>
                    </button>
                    <button
                      onClick={() => gotoPage(pageCount - 1)}
                      disabled={!canNextPage}
                      className={`relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium ${!canNextPage ? 'text-gray-300' : 'text-gray-500 hover:bg-gray-50'}`}
                    >
                      <span className="sr-only">Last</span>
                      <span>»</span>
                    </button>
                  </nav>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
} 